import React, { useState, useEffect } from "react";

// material import
import { Box, Card, Chip, CircularProgress, Grid2, Typography } from "@mui/material";

// project import
import AdminDBServices from "../../services/v1/adminDBServices";
import { todayISO, formatDateForDisplay } from "../../utils/dateUtils";

const TodayDonation = () => {
  const [donationData, setDonationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const PAYMENT_STATUS = {
    0: "Pending",
    1: "Paid",
  };

  const PACKAGES = {
    1: "Sponsor a Day",
    2: "Sponsor LifeTime",
  };

  const FREQUENCIES = {
    1: "Monthly",
    2: "Yearly",
  };

  const TYPE = {
    1: "Recurring",
    2: "One-Time",
  };

  const fetchTodayDonations = async () => {
    try {
      setLoading(true);
      const date = todayISO();
      const response = await AdminDBServices.todayDonations(date);

      // Check if response has data and update state
      if (response?.data?.data.length > 0) {
        setDonationData(response.data.data[0]); // response.data is already an array of donations
      } else {
        setError("No donations found for today.");
      }
    } catch (err) {
      setError("Failed to fetch today's donations.");
      console.error("Error fetching today's donations:", err);
    }finally{
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTodayDonations();
  }, []);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="20vh"
      >
        <CircularProgress color="secondary" />
      </Box>
    );
  }

  if (!donationData) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <Typography variant="body1">No donations found for today.</Typography>
      </Box>
    );
  }

  // Destructure necessary data from the API response
  const {
    fullname,
    donor,
    reservation_number,
    message,
    packageID,
    paymentMethodID,
    payment_status,
    frequencyID,
    date,
    toDate,
    typeID,
  } = donationData;

  // Get labels based on ID values
  const paymentStatus = PAYMENT_STATUS[payment_status] || "N/A";
  const packageLabel = PACKAGES[packageID] || "N/A";
  const frequencyLabel = FREQUENCIES[frequencyID] || "-";
  const typeLabel = TYPE[typeID] || "N/A";

  const title = donor?.prof_title;
  const donorName = donor?.fullname || "N/A";
  const donorEmail = donor?.email || "N/A";
  const donorContact = donor?.contactNo || "N/A";
  const donorWhatsApp = donor?.whatsappNo || "-";
  const donorCity = donor?.city || "N/A";
  const donorCountry = donor?.country_name || "N/A";

  const formattedDate = formatDateForDisplay(date);
  const formattedToDate = toDate ? formatDateForDisplay(toDate) : '';

  return (
    <Box>
      <Card>
        <Grid2 container spacing={1} p={5}>
          <Grid2 item size={{ xs: 5.8 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Reservation No
            </Typography>
          </Grid2>
          <Grid2 item size={{ xs: 5.8 }}>
            <Typography variant="body2">
              {reservation_number || "N/A"}
            </Typography>
          </Grid2>
          <Grid2 item size={{ xs: 0.4 }} />

          <Grid2 item size={{ xs: 5.8 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Donor Name
            </Typography>
          </Grid2>
          <Grid2 item size={{ xs: 5.8 }}>
            <Typography variant="body2">
              {title} {donorName}
            </Typography>
          </Grid2>
          <Grid2 item size={{ xs: 0.4 }} />

          <Grid2 item size={{ xs: 5.8 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Email
            </Typography>
          </Grid2>
          <Grid2 item size={{ xs: 5.8 }}>
            <Typography variant="body2">{donorEmail}</Typography>
          </Grid2>
          <Grid2 item size={{ xs: 0.4 }} />

          <Grid2 item size={{ xs: 5.8 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Contact No
            </Typography>
          </Grid2>
          <Grid2 item size={{ xs: 5.8 }}>
            <Typography variant="body2">{donorContact}</Typography>
          </Grid2>
          <Grid2 item size={{ xs: 0.4 }} />

          <Grid2 item size={{ xs: 5.8 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              WhatsApp No
            </Typography>
          </Grid2>
          <Grid2 item size={{ xs: 5.8 }}>
            <Typography variant="body2">{donorWhatsApp}</Typography>
          </Grid2>
          <Grid2 item size={{ xs: 0.4 }} />

          <Grid2 item size={{ xs: 5.8 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              City
            </Typography>
          </Grid2>
          <Grid2 item size={{ xs: 5.8 }}>
            <Typography variant="body2">{donorCity}</Typography>
          </Grid2>
          <Grid2 item size={{ xs: 0.4 }} />

          <Grid2 item size={{ xs: 5.8 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Country
            </Typography>
          </Grid2>
          <Grid2 item size={{ xs: 5.8 }}>
            <Typography variant="body2">{donorCountry}</Typography>
          </Grid2>
          <Grid2 item size={{ xs: 0.4 }} />

          <Grid2 item size={{ xs: 5.8 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Payment Status
            </Typography>
          </Grid2>
          <Grid2 item size={{ xs: 5.8 }}>
            <Chip
              label={paymentStatus}
              size="small"
              color={paymentStatus === "Paid" ? "success" : "warning"}
            />
          </Grid2>
          <Grid2 item size={{ xs: 0.4 }} />

          <Grid2 item size={{ xs: 5.8 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Package
            </Typography>
          </Grid2>
          <Grid2 item size={{ xs: 5.8 }}>
            <Typography variant="body2">{packageLabel}</Typography>
          </Grid2>
          <Grid2 item size={{ xs: 0.4 }} />

          <Grid2 item size={{ xs: 5.8 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Frequency
            </Typography>
          </Grid2>
          <Grid2 item size={{ xs: 5.8 }}>
            <Typography variant="body2">{frequencyLabel || "-"}</Typography>
          </Grid2>

          <Grid2 item size={{ xs: 0.4 }} />

          <Grid2 item size={{ xs: 5.8 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Type
            </Typography>
          </Grid2>
          <Grid2 item size={{ xs: 5.8 }}>
            <Typography variant="body2">{typeLabel}</Typography>
          </Grid2>

          <Grid2 item size={{ xs: 0.4 }} />

          <Grid2 item size={{ xs: 5.8 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {typeID === 1 ? "Recurring From" : "Booking Date"}
            </Typography>
          </Grid2>
          <Grid2 item size={{ xs: 5.8 }}>
            <Typography variant="body2">
              {typeID === 1 ? formattedDate : formattedDate}
            </Typography>
          </Grid2>
          <Grid2 item size={{ xs: 0.4 }} />

          {typeID === 1 && (
            <>
              <Grid2 item size={{ xs: 5.8 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Recurring To
                </Typography>
              </Grid2>
              <Grid2 item size={{ xs: 5.8 }}>
                <Typography variant="body2">
                  {formattedToDate ? formattedToDate : "N/A"}
                </Typography>
              </Grid2>
              <Grid2 item size={{ xs: 0.4 }} />
            </>
          )}

          <Grid2 item size={{ xs: 5.8 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Purpose for Sponsoring
            </Typography>
          </Grid2>
          <Grid2 item size={{ xs: 5.8 }}>
            <Typography variant="body2">{message}</Typography>
          </Grid2>
        </Grid2>
      </Card>
    </Box>
  );
};

export default TodayDonation;
